import React, { useEffect, useMemo, useState } from 'react';
import { Check, HelpCircle, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, Button, cn } from '../../ui';
import IconPicker from './IconPicker';
import FieldSchemaEditor, { isKnownFieldType } from './FieldSchemaEditor';
import ConfirmDialog from './ConfirmDialog';
import HelpTip from './HelpTip';
import { canonicalType } from '../fieldTypeAliases';
import { api } from '../api';
import { isSuperAdmin } from '../auth';
import { camposDeUbicacion } from '../utils/ubicacion';

// Los campos de UBICACIÓN: sólo los pone y los quita el interruptor «Seguir su ubicación» (Fase 3b).
const NOMBRES_UBICACION = ['provincia', 'localidad'];

/**
 * TipoForm
 *
 * Formulario crear/editar tipo. Replica el sneak peek validado del
 * mockup v3 (Camiseta + drag-and-drop + select):
 *
 *   - Scope (inmutable en edición), Nombre, Slug (auto), Categoría, Icono
 *   - ¿Gestionado con stock? (toggle)
 *   - Referencia: prefijo (auto-sugerido, validación visual), dígitos del
 *     contador, preview de la primera referencia
 *   - Esquema de campos con drag-and-drop y tipo select (FieldSchemaEditor)
 *
 * Props:
 *   mode: 'crear' | 'editar'
 *   initial?: tipo existente (modo editar)
 *   onCancel: () => void
 *   onSubmit: (payload) => Promise   (lanza si error; el caller captura)
 *   suggestPrefix: (slug) => Promise<string>
 */

function slugify(nombre) {
  return String(nombre || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 20);
}

const EMPTY = {
  scope: 'interno',
  nombre: '',
  slug: '',
  categoria_id: null,
  icon: 'box',
  has_stock: 1,
  tiene_coste: false,
  naturaleza: 'material',
  una_por_persona: 'permitir',
  ref_prefix: '',
  ref_padding: 4,
  fields_schema: [],
};

export default function TipoForm({
  mode,
  initial,
  onCancel,
  onSubmit,
  suggestPrefix,
  reloadKey = 0,
}) {
  const isEdit = mode === 'editar';
  const [form, setForm] = useState(() =>
    // Aceptamos `initial` también en modo crear: TiposList lo usa
    // para pre-rellenar `scope` con la pestaña activa, de modo que
    // un alta desde el tab "Colaboradores" no salga como interno por
    // arrastre del default de EMPTY. En edición sigue siendo el tipo
    // completo cargado del backend.
    initial ? { ...EMPTY, ...initial } : { ...EMPTY }
  );
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [prefixTouched, setPrefixTouched] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [categorias, setCategorias] = useState([]);
  // Interruptor «Seguir su ubicación» (sólo Super Admin, tipo ya creado). Se guarda AL PULSARLO, no
  // con «Guardar cambios»: añade o quita las columnas en el momento.
  const [ubicActiva, setUbicActiva] = useState(() => !!camposDeUbicacion(initial && initial.fields_schema));
  const [ubicBusy, setUbicBusy] = useState(false);
  const [ubicMsg, setUbicMsg] = useState(null); // { ok, texto }
  const puedeInterruptor = isEdit && !!(initial && initial.id) && isSuperAdmin();
  // Movimientos que ha tenido el tipo, CONTANDO LOS BORRADOS: lo calcula el servidor con la misma
  // función que decide el 409 (assetTypeService.contarMovimientos). Con alguno, no se desactiva.
  const conMovimientos = Number((initial && initial.movimientos_registrados) || 0) > 0;
  const [confirmarQuitar, setConfirmarQuitar] = useState(false);

  /** Llama al servidor y deja el esquema del formulario como el suyo. Lanza si el servidor dice que no. */
  async function aplicarUbicacion(activar) {
    setUbicBusy(true);
    setUbicMsg(null);
    try {
      const r = await api.put(`/tipos/${initial.id}/ubicacion`, { activar });
      const delServidor = ((r && r.tipo && r.tipo.fields_schema) || []).filter((x) => NOMBRES_UBICACION.includes(x.name));
      // El resto del esquema se queda como esté en el formulario (puede haber cambios sin guardar).
      setForm((f) => ({
        ...f,
        fields_schema: [...f.fields_schema.filter((x) => !NOMBRES_UBICACION.includes(x.name)), ...delServidor],
      }));
      setUbicActiva(activar);
      setUbicMsg({ ok: true, texto: activar
        ? 'Hecho: el tipo tiene Provincia y Localidad, y la acción «Registrar movimiento».'
        : 'Hecho: el tipo ya no tiene Provincia ni Localidad.' });
    } finally {
      setUbicBusy(false);
    }
  }

  function pulsarInterruptor(activar) {
    if (!activar) { setConfirmarQuitar(true); return; }
    aplicarUbicacion(true).catch((err) => setUbicMsg({
      ok: false, texto: err?.response?.data?.error || err?.response?.data?.message || err.message,
    }));
  }

  useEffect(() => {
    let cancel = false;
    api.get('/categorias')
      .then((r) => {
        if (cancel) return;
        const list = (r && r.data && r.data.data) || (r && r.data) || [];
        setCategorias(Array.isArray(list) ? list : []);
      })
      .catch(() => { /* silencioso: el form sigue, sin categorías */ });
    return () => { cancel = true; };
  }, [reloadKey]);

  // Slug auto desde nombre (hasta que el usuario lo toque)
  useEffect(() => {
    if (!slugTouched && !isEdit) {
      setForm((f) => ({ ...f, slug: slugify(f.nombre) }));
    }
  }, [form.nombre, slugTouched, isEdit]);

  // Prefijo auto-sugerido desde slug (hasta que el usuario lo toque)
  useEffect(() => {
    let cancel = false;
    if (!prefixTouched && !isEdit && form.slug) {
      suggestPrefix(form.slug)
        .then((p) => {
          if (!cancel && p) setForm((f) => ({ ...f, ref_prefix: p }));
        })
        .catch(() => {});
    }
    return () => {
      cancel = true;
    };
  }, [form.slug, prefixTouched, isEdit, suggestPrefix]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const prefixValid = /^[A-Z]{1,5}$/.test(form.ref_prefix || '');
  const firstRef = useMemo(() => {
    if (!prefixValid) return '—';
    const counter = isEdit ? (initial?.ref_counter || 0) + 1 : 1;
    return `${form.ref_prefix}-${String(counter).padStart(Number(form.ref_padding) || 4, '0')}`;
  }, [form.ref_prefix, form.ref_padding, prefixValid, isEdit, initial]);

  // Validación de envío
  const errors = [];
  if (!form.nombre.trim()) errors.push('El nombre es obligatorio');
  if (!form.slug || !/^[a-z][a-z0-9_]*$/.test(form.slug))
    errors.push('Slug inválido (snake_case en minúsculas)');
  if (!prefixValid) errors.push('Prefijo inválido (1-5 letras A-Z)');
  form.fields_schema.forEach((f, i) => {
    if (!f.name) errors.push(`Campo #${i + 1}: falta el nombre`);
    if (NOMBRES_UBICACION.includes(f.name) && !ubicActiva)
      errors.push(`Campo "${f.label || f.name}": «${f.name}» es un campo de ubicación; lo pone el interruptor «Seguir su ubicación» (Super Admin). Cambia la etiqueta.`);
    // Bloquea el guardado si el tipo (ya canonicalizado) no es uno que el
    // editor reconoce: evita persistir un tipo mutado (p. ej. reescribir un
    // *_link como string). El usuario debe elegir un tipo válido.
    if (!isKnownFieldType(canonicalType(f.type)))
      errors.push(`Campo "${f.name || i + 1}": tipo no reconocido "${f.type}". Elige un tipo válido.`);
    if (f.type === 'select' && (!f.options || f.options.filter(Boolean).length === 0))
      errors.push(`Campo "${f.name || i + 1}": el select necesita al menos una opción`);
  });
  const canSubmit = errors.length === 0 && !submitting;

  async function handleSubmit() {
    setServerError(null);
    setSubmitting(true);
    try {
      // Limpiar opciones vacías de los selects antes de enviar
      // El backend exige `label` por campo; el editor no lo captura,
      // así que se deriva del `name` (snake_case → "Texto legible").
      // Si en el futuro se añade un input de etiqueta al editor,
      // bastará con respetar f.label cuando venga informado.
      const humanize = (s) =>
        String(s || '')
          .replace(/[_-]+/g, ' ')
          .trim()
          .replace(/^\w/, (c) => c.toUpperCase());

      // Payload con whitelist explícita: NO se envían campos
      // server-managed como `ref_counter` (que el backend rechaza con
      // 400: "ref_counter no es editable: lo gestiona el alta de
      // activos"), ni `id`/`created_at`/`updated_at`. `slug` y `scope`
      // se envían tal cual: el backend los acepta si coinciden con el
      // valor actual (son inmutables tras crear).
      const payload = {
        scope: form.scope,
        nombre: form.nombre,
        slug: form.slug,
        categoria_id: form.categoria_id == null ? null : Number(form.categoria_id),
        icon: form.icon,
        has_stock: form.has_stock,
        tiene_coste: !!form.tiene_coste,
        naturaleza: form.naturaleza === 'licencia' ? 'licencia' : 'material',
        una_por_persona: ['avisar', 'bloquear'].includes(form.una_por_persona) ? form.una_por_persona : 'permitir',
        ref_prefix: form.ref_prefix,
        ref_padding: form.ref_padding,
        fields_schema: form.fields_schema.map((f) => {
          const base = {
            ...f,
            // El editor guarda el tipo CANÓNICO (empresa_link → company_link),
            // el mismo que persiste el backend, para no reintroducir la
            // divergencia alias/canónico que rompía el round-trip.
            type: canonicalType(f.type),
            label: (f.label && String(f.label).trim()) || humanize(f.name),
          };
          return f.type === 'select'
            ? { ...base, options: (f.options || []).map((o) => o.trim()).filter(Boolean) }
            : base;
        }),
      };
      await onSubmit(payload);
    } catch (err) {
      setServerError(
        err?.response?.data?.error ||
          err?.message ||
          'Error al guardar el tipo'
      );
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <div className="p-4 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-900">
          {isEdit ? `Editar tipo · ${initial?.nombre}` : 'Crear tipo nuevo'}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          {isEdit
            ? 'El slug y el scope no se pueden cambiar tras crear el tipo'
            : 'Define la estructura del tipo y su esquema de campos'}
        </p>
      </div>

      <CardContent className="p-4">
        {/* Datos básicos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div>
            <label className="text-slate-500 mb-1 flex items-center gap-1.5">
              Scope
              <HelpTip>Interno = personal GRUPOLAE; Colaboradores = externos. Cada uno tiene sus propios activos.</HelpTip>
            </label>
            <select
              value={form.scope}
              disabled={isEdit}
              onChange={(e) => set({ scope: e.target.value })}
              className={cn(
                'w-full border border-slate-200 rounded-md px-3 py-2 bg-white',
                isEdit && 'bg-slate-50 text-slate-500 cursor-not-allowed'
              )}
            >
              <option value="interno">Interno · GRUPOLAE</option>
              <option value="colab">Colaboradores</option>
            </select>
          </div>

          <div>
            <label className="text-slate-500 mb-1 block">Nombre del tipo</label>
            <input
              value={form.nombre}
              onChange={(e) => set({ nombre: e.target.value })}
              placeholder="Camiseta"
              className="w-full border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="text-slate-500 mb-1 block">
              Slug <span className="text-slate-400">(auto)</span>
            </label>
            <input
              value={form.slug}
              disabled={isEdit}
              onChange={(e) => {
                setSlugTouched(true);
                set({ slug: slugify(e.target.value) });
              }}
              className={cn(
                'w-full border border-slate-200 rounded-md px-3 py-2 font-mono text-slate-600',
                isEdit ? 'bg-slate-50 cursor-not-allowed' : 'bg-slate-50'
              )}
            />
          </div>

          <div>
            <label className="text-slate-500 mb-1 block">Categoría</label>
            <select
              value={form.categoria_id ?? ''}
              onChange={(e) =>
                set({ categoria_id: e.target.value ? Number(e.target.value) : null })
              }
              className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white"
            >
              <option value="">Sin categoría</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-slate-500 mb-1 block">Icono</label>
            <IconPicker
              value={form.icon}
              onChange={(name) => set({ icon: name })}
            />
          </div>

          <div>
            <label className="text-slate-500 mb-1 flex items-center gap-1.5">
              ¿Gestionado con stock?
              <HelpTip>Actívalo si controlas unidades en almacén; podrás registrar compras y reglas de alerta de mínimo.</HelpTip>
            </label>
            <button
              type="button"
              onClick={() => set({ has_stock: form.has_stock ? 0 : 1 })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white flex items-center gap-2"
            >
              <span
                className={cn(
                  'w-9 h-5 rounded-full relative transition-colors',
                  form.has_stock ? 'bg-[hsl(var(--brand-light))]' : 'bg-slate-300'
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all',
                    form.has_stock ? 'right-0.5' : 'left-0.5'
                  )}
                />
              </span>
              <span className="text-slate-700">{form.has_stock ? 'Sí' : 'No'}</span>
            </button>
          </div>

          <div>
            <label className="text-slate-500 mb-1 flex items-center gap-1.5">
              Los activos de este tipo tienen coste
              <HelpTip>Cada activo tendrá un campo de coste que suma al valor del inventario.</HelpTip>
            </label>
            <button
              type="button"
              onClick={() => set({ tiene_coste: !form.tiene_coste })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white flex items-center gap-2"
            >
              <span
                className={cn(
                  'w-9 h-5 rounded-full relative transition-colors',
                  form.tiene_coste ? 'bg-[hsl(var(--brand-light))]' : 'bg-slate-300'
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all',
                    form.tiene_coste ? 'right-0.5' : 'left-0.5'
                  )}
                />
              </span>
              <span className="text-slate-700">{form.tiene_coste ? 'Sí' : 'No'}</span>
            </button>
            <p className="text-[10px] text-slate-400 mt-1">
              Si lo activas, cada activo tendrá un campo de coste y sumará al
              valor del inventario en el dashboard.
            </p>
          </div>

          {/* Material o licencia (18/10): decide el verbo de la tarea de una baja. Un portátil se
              recupera; una licencia se cancela o se libera. */}
          <div>
            <label className="text-slate-500 mb-1 flex items-center gap-1.5" htmlFor="tipo-naturaleza">
              Qué es
              <HelpTip>Lo lee la baja: para el material pide «Recuperar»; para una licencia, «Cancelar o liberar».</HelpTip>
            </label>
            <select id="tipo-naturaleza" value={form.naturaleza || 'material'} onChange={(e) => set({ naturaleza: e.target.value })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white text-slate-700">
              <option value="material">Material (se entrega y se recupera)</option>
              <option value="licencia">Licencia (se asigna y se cancela o se libera)</option>
            </select>
          </div>

          {/* Una por persona (01/11): qué pasa al asignar a alguien una segunda unidad de ESTE tipo.
              Cada ámbito tiene el suyo: el portátil de GRUPOLAE y el de colaboradores van aparte. */}
          <div>
            <label className="text-slate-500 mb-1 flex items-center gap-1.5" htmlFor="tipo-una-por-persona">
              Unidades por persona
              <HelpTip>Al asignar a alguien que ya tiene uno de este tipo. «Avisar» pregunta antes de asignar y deja constancia en el historial; «Bloquear» no deja: hay que devolver el que tiene. No se puede pasar a «Bloquear» si alguien tiene ya dos o más.</HelpTip>
            </label>
            <select id="tipo-una-por-persona" value={form.una_por_persona || 'permitir'} onChange={(e) => set({ una_por_persona: e.target.value })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white text-slate-700">
              <option value="permitir">Permitir varias (sin aviso)</option>
              <option value="avisar">Avisar si ya tiene uno</option>
              <option value="bloquear">Bloquear: solo uno por persona</option>
            </select>
          </div>
        </div>

        {/* Referencia interna */}
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-5 mb-2">
          Referencia interna
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div>
            <label className="text-slate-500 mb-1 flex items-center gap-1.5">
              Siglas (prefix)
              <span className="text-slate-400 text-[10px]">1-5 letras A-Z</span>
            </label>
            <div
              className={cn(
                'border-2 rounded-md px-3 py-2 bg-white font-mono text-slate-900 flex items-center justify-between',
                prefixValid ? 'border-[hsl(var(--brand-light))]' : 'border-rose-300'
              )}
            >
              <input
                value={form.ref_prefix}
                disabled={isEdit && (initial?.unidades || 0) > 0}
                onChange={(e) => {
                  setPrefixTouched(true);
                  set({ ref_prefix: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5) });
                }}
                className="flex-1 bg-transparent focus:outline-none disabled:cursor-not-allowed"
              />
              {prefixValid && (
                <span className="text-[10px] text-emerald-700 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  OK
                </span>
              )}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">
              {isEdit && (initial?.unidades || 0) > 0
                ? 'Bloqueado: el tipo ya tiene activos'
                : 'Sugerencia automática desde el slug. Puedes cambiarlo.'}
            </div>
          </div>

          <div>
            <label className="text-slate-500 mb-1 block">Dígitos del contador</label>
            <select
              value={form.ref_padding}
              onChange={(e) => set({ ref_padding: Number(e.target.value) })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white"
            >
              {[3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-slate-400 mt-1">
              Cubre hasta {('9'.repeat(Number(form.ref_padding) || 4))} unidades.
            </div>
          </div>

          <div>
            <label className="text-slate-500 mb-1 block">
              Primera referencia que se generará
            </label>
            <div className="border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2 font-mono text-emerald-900 font-semibold">
              {firstRef}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">
              El prefijo queda bloqueado al crearse el primer activo.
            </div>
          </div>
        </div>

        {/* Esquema de campos */}
        <div className="mt-5">
          {puedeInterruptor && (
            <div className="mb-3 border border-slate-200 rounded-md p-3 text-xs">
              <label className="flex items-center gap-2 font-medium text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={ubicActiva}
                  disabled={ubicBusy || (ubicActiva && conMovimientos)}
                  onChange={(e) => pulsarInterruptor(e.target.checked)}
                />
                Seguir su ubicación
                {ubicBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
              </label>
              <p className="mt-1 text-slate-500">
                Añade Provincia (las 52 del INE) y Localidad (municipio del INE de esa provincia), y la acción
                «Registrar movimiento». Se aplica al pulsarlo. Quitarlo sólo se puede si el tipo nunca ha tenido un movimiento.
              </p>
              {ubicActiva && conMovimientos && (
                <p className="mt-1 text-slate-600 font-medium">
                  No se puede desactivar: este tipo ya tiene movimientos registrados.
                </p>
              )}
              {ubicMsg && (
                <p className={cn('mt-1', ubicMsg.ok ? 'text-emerald-700' : 'text-rose-700')}>{ubicMsg.texto}</p>
              )}
            </div>
          )}
          <ConfirmDialog
            open={confirmarQuitar}
            onClose={() => setConfirmarQuitar(false)}
            titulo={`Dejar de seguir la ubicación de «${(initial && initial.nombre) || ''}»`}
            textoConfirmar="Dejar de seguirla"
            destructivo
            onConfirm={async () => { await aplicarUbicacion(false); setConfirmarQuitar(false); }}
          >
            <p>
              Se quitarán los campos <span className="font-medium">Provincia</span> y{' '}
              <span className="font-medium">Localidad</span> de este tipo, y sus columnas en la base de datos.
            </p>
            <p>
              Los activos de este tipo dejarán de mostrar su ubicación, y desaparecerá la acción
              «Registrar movimiento».
            </p>
          </ConfirmDialog>
          <FieldSchemaEditor
            value={form.fields_schema}
            onChange={(fields_schema) => set({ fields_schema })}
            bloqueados={ubicActiva ? NOMBRES_UBICACION : []}
          />
        </div>

        {/* Errores */}
        {(errors.length > 0 || serverError) && (
          <div className="mt-4 bg-rose-50 border border-rose-200 rounded-md p-3 text-xs text-rose-700">
            <div className="flex items-center gap-2 font-medium mb-1">
              <AlertCircle className="w-3.5 h-3.5" />
              {serverError ? 'Error al guardar' : 'Revisa estos puntos antes de guardar'}
            </div>
            {serverError ? (
              <p>{serverError}</p>
            ) : (
              <ul className="list-disc pl-5 space-y-0.5">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Acciones */}
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {isEdit ? 'Guardar cambios' : 'Crear tipo'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
